import { Injectable, signal, computed } from '@angular/core';
import { UserSession } from 'app/features/auth/interface/auth-interface';

type StorageType = 'session' | 'local';

@Injectable({ providedIn: 'root' })
export class Session {

  private readonly SESSION_KEY = 'user_session';
  private readonly STORAGE_TYPE_KEY = 'session_storage_type';

  private storageType: StorageType = 'session';
  private _session = signal<UserSession | null>(null);

  readonly session = this._session.asReadonly();
  readonly isLoggedInSignal = computed(() => !!this._session()?.access_token);

  constructor() {
    this.storageType = this.loadStorageType();
    this._session.set(this.loadSession());
  }

  /** ---------------------------- STORAGE HANDLER ---------------------------- */

  private get storage(): Storage {
    return this.storageType === 'local' ? localStorage : sessionStorage;
  }

  /** Load selected storage type */
  private loadStorageType(): StorageType {
    const saved = localStorage.getItem(this.STORAGE_TYPE_KEY) as StorageType | null;
    return saved === 'local' || saved === 'session' ? saved : 'session';
  }

  /** ---------------------------- SESSION MANAGEMENT ---------------------------- */

  setSession(data: UserSession, type: StorageType = 'session'): void {
    if (!data?.access_token) {
      throw new Error('Invalid session data');
    }

    this.storageType = type;
    localStorage.setItem(this.STORAGE_TYPE_KEY, type); // Persist selected storage type

    this.storage.setItem(this.SESSION_KEY, JSON.stringify(data));
    this._session.set(data);
  }

  private loadSession(): UserSession | null {
    const raw = this.storage.getItem(this.SESSION_KEY);
    try {
      return raw ? JSON.parse(raw) : null;
    } catch {
      this.clearSession();
      return null;
    }
  }

  /** ---------------------------- GETTERS ---------------------------- */

  getSession(): UserSession | null {
    return this._session();
  }

  getToken(): string | null {
    return this._session()?.access_token ?? null;
  }

  getRefreshToken(): string | null {
    return this._session()?.refresh_token ?? null;
  }

  /** ---------------------------- CLEAR SESSION ---------------------------- */

  clearSession(): void {
    localStorage.removeItem(this.SESSION_KEY);
    sessionStorage.removeItem(this.SESSION_KEY);
    localStorage.removeItem(this.STORAGE_TYPE_KEY);

    this._session.set(null);
  }

  /** ---------------------------- UTIL ---------------------------- */

  isLoggedIn(): boolean {
    return !!this.getToken();
  }
}
